import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Autocomplete,
  Box,
  Typography,
  Collapse,
  IconButton,
  Chip,
  TablePagination,
  CircularProgress,
  Alert,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Badge,
  Avatar,
  Divider,
} from "@mui/material";
import {
  RemoveRedEye as RemoveRedEyeIcon,
  Info as InfoIcon,
  Add as AddIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Calculate as CalculateIcon,
  Search as SearchIcon,
  AccountCircle,
  AttachMoney,
  Receipt,
  TrendingDown,
  LocalAtm,
  AddCircleOutline,
} from "@mui/icons-material";
import GavelIcon from "@mui/icons-material/Gavel";
import URLs from "constants/urls";
import Cookies from "js-cookie";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import CommentIcon from "@mui/icons-material/Comment";
import PropTypes from "prop-types";

// Color theme with better contrast
const COLORS = {
  charcoal: "#1d2228e1",
  white: "#FFFFFF",
  orange: "#FF6B35",
  lightGray: "#F8F9FA",
  darkGray: "#6C757D",
  primary: "#3498DB",
  success: "#27AE60",
  error: "#E74C3C",
  warning: "#F39C12",
};

const InvoiceEditor = ({ dsaId = 0 }) => {
  const [invoices, setInvoices] = useState([]);
  const [allDisbursements, setAllDisbursements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [selectedRecoveries, setSelectedRecoveries] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(4);
  const [saving, setSaving] = useState(false);
  const [saveDialog, setSaveDialog] = useState({ open: false, invoiceNo: null });
  const [loadingRecoveries, setLoadingRecoveries] = useState({});
  const [penaltyAmounts, setPenaltyAmounts] = useState({});
  const [additionalPayoutAmounts, setAdditionalPayoutAmounts] = useState({});
  const [remarksData, setRemarksData] = useState({});

  // New state for additional payout

  // Fetch initial data
  useEffect(() => {
    if (dsaId === undefined || dsaId === null) return; // wait for user.companyId to load

    const fetchData = async () => {
      try {
        setLoading(true);
        await Promise.all([fetchInvoices(), fetchAllDisbursements()]);
      } catch (err) {
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dsaId]);

  const fetchInvoices = async () => {
    try {
      const token = Cookies.get("jwt_token");
      if (!token) throw new Error("Authentication token not found");
      const url =
        dsaId && dsaId !== 0 ? `${URLs.dsaReceiptsUrl}=${dsaId}` : URLs.getYetToReceiveInvoices;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error(`Failed to fetch invoices (${res.status})`);

      const result = await res.json();
      const invoiceList = result?.data || result || [];

      const normalizedInvoices = invoiceList.map((invoice) => {
        // Ensure recoveryDisburseIds is an array
        let recoveryDisburseIds = [];
        try {
          if (Array.isArray(invoice.recoveryDisburseIds)) {
            recoveryDisburseIds = invoice.recoveryDisburseIds;
          } else if (typeof invoice.recoveryDisburseIds === "string") {
            recoveryDisburseIds = JSON.parse(invoice.recoveryDisburseIds || "[]");
          }
        } catch {
          recoveryDisburseIds = [];
        }

        return {
          ...invoice,
          recoveryDisburseIds,
          amount: Number(invoice.amount) || 0,
          tdsPercent: Number(invoice.tdsPercent) || 0,
          tdsAmount: Number(invoice.tdsAmount) || 0,
          netDsaAmount: Number(invoice.netDsaAmount) || 0,
        };
      });

      setInvoices(normalizedInvoices);

      // Initialize empty selected recoveries
      const initialSelected = {};
      const initialPenalty = {};
      const initialAdditionalPayout = {};
      normalizedInvoices.forEach((invoice) => {
        initialSelected[invoice.invoiceNo] = [];
        initialPenalty[invoice.invoiceNo] = 0;
        initialAdditionalPayout[invoice.invoiceNo] = 0;
      });
      setSelectedRecoveries(initialSelected);
      setPenaltyAmounts(initialPenalty);
      setAdditionalPayoutAmounts(initialAdditionalPayout);
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const handlePenaltyChange = (invoiceNo, value) => {
    const penaltyValue = Math.max(0, Number(value) || 0);
    setPenaltyAmounts((prev) => ({
      ...prev,
      [invoiceNo]: penaltyValue,
    }));
  };
  const handleRemarksChange = (invoiceNo, value) => {
    setRemarksData((prev) => ({
      ...prev,
      [invoiceNo]: value,
    }));
  };

  const handleAdditionalPayoutChange = (invoiceNo, value) => {
    const payoutValue = Math.max(0, Number(value) || 0);
    setAdditionalPayoutAmounts((prev) => ({
      ...prev,
      [invoiceNo]: payoutValue,
    }));
  };

  const fetchAllDisbursements = async () => {
    try {
      const token = Cookies.get("jwt_token");
      if (!token) throw new Error("Authentication token not found");

      const url =
        dsaId && dsaId !== 0
          ? `${URLs.dmaAmountReceivedInvoices}?dsaId=${dsaId}`
          : URLs.getRecievedDibursements;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error(`Failed to fetch disbursements (${res.status})`);

      const result = await res.json();
      const disbursementList = result?.data || result || [];

      const normalizedDisbursements = disbursementList.map((item) => ({
        disburseId: item.disburseId ?? item.invoiceNo, // fallback since disburseId is null in sample
        applicationNumber: item.applicationNumber || item.invoiceDisplayNumber,
        customerName: item.dsaName || "Unknown Customer",
        grossPayoutAmount: Number(item.amount) || 0,
        providerName: item.finainacialInstitutionName,
        grossPayoutPercent: Number(item.tdsPercent) || 0,
      }));

      setAllDisbursements(normalizedDisbursements);
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const handleExpandRow = async (invoiceNo) => {
    if (expandedRow === invoiceNo) {
      setExpandedRow(null);
    } else {
      setExpandedRow(invoiceNo);

      const invoice = invoices.find((inv) => inv.invoiceNo === invoiceNo);
      if (invoice) {
        setLoadingRecoveries((prev) => ({ ...prev, [invoiceNo]: true }));

        // Set timeout to simulate loading
        setTimeout(() => {
          // Find disbursements that match the recoveryDisburseIds
          const existingSelections = allDisbursements.filter((disbursement) =>
            invoice.recoveryDisburseIds?.includes(disbursement.disburseId)
          );

          setSelectedRecoveries((prev) => ({
            ...prev,
            [invoiceNo]: existingSelections,
          }));

          setLoadingRecoveries((prev) => ({ ...prev, [invoiceNo]: false }));
        }, 300);
      }
    }
  };

  const handleViewCreditNoteInvoicePdf = async (invoiceNo) => {
    const token = Cookies.get("jwt_token");
    try {
      if (!invoiceNo) {
        throw new Error("Invoice number is missing");
      }

      const response = await fetch(`${URLs.dsaCreditNoteInvoiceView}/${invoiceNo}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch invoice");
      }

      const blob = await response.blob();
      const pdfUrl = URL.createObjectURL(blob);

      // Open in a new tab
      window.open(pdfUrl, "_blank");
    } catch (error) {
      console.error("Error fetching invoice:", error);
    }
  };

  const handleViewInvoicePdf = async (invoiceNo) => {
    const token = Cookies.get("jwt_token");
    try {
      if (!invoiceNo) {
        throw new Error("Invoice number is missing");
      }

      const response = await fetch(`${URLs.headOfficeInvoiceView}/${invoiceNo}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch invoice");
      }

      const blob = await response.blob();
      const pdfUrl = URL.createObjectURL(blob);

      // Open in a new tab
      window.open(pdfUrl, "_blank");
    } catch (error) {
      console.error("Error fetching invoice:", error);
    }
  };

  const handleRecoverySelect = (invoiceNo, newValue) => {
    setSelectedRecoveries((prev) => ({
      ...prev,
      [invoiceNo]: newValue,
    }));
  };

  const handleRemoveRecovery = (invoiceNo, disburseId) => {
    setSelectedRecoveries((prev) => ({
      ...prev,
      [invoiceNo]: prev[invoiceNo].filter((item) => item.disburseId !== disburseId),
    }));
  };

  const calculateSummary = (invoice, selected, penaltyAmount = 0, additionalPayoutAmount = 0) => {
    if (!selected || selected.length === 0) {
      const totalDeductions = penaltyAmount;
      const amountAfterDeductions = Math.max(
        0,
        invoice.amount - totalDeductions + additionalPayoutAmount
      );
      const gst = amountAfterDeductions * 0.18;
      const totalAmount = amountAfterDeductions + gst;

      return {
        totalGrossPayout: 0,
        penaltyAmount,
        additionalPayoutAmount,
        totalDeductions,
        amountAfterDeductions,
        gst,
        totalAmount,
      };
    }

    const totalGrossPayout = selected.reduce((sum, item) => sum + (item.grossPayoutAmount || 0), 0);
    const totalDeductions = totalGrossPayout + penaltyAmount;
    const amountAfterDeductions = Math.max(
      0,
      invoice.amount - totalDeductions + additionalPayoutAmount
    );
    const gst = amountAfterDeductions * 0.18;
    const totalAmount = amountAfterDeductions + gst;

    return {
      totalGrossPayout,
      penaltyAmount,
      additionalPayoutAmount,
      totalDeductions,
      amountAfterDeductions,
      gst,
      totalAmount,
    };
  };

  const getAvailableDisbursements = (invoiceNo) => {
    const invoice = invoices.find((inv) => inv.invoiceNo === invoiceNo);
    if (!invoice) return [];

    const currentSelections = selectedRecoveries[invoiceNo] || [];
    const selectedIds = currentSelections.map((item) => item.disburseId);

    return allDisbursements.filter(
      (disbursement) => !selectedIds.includes(disbursement.disburseId)
    );
  };

  const handleSave = async (invoiceNo) => {
    const selected = selectedRecoveries[invoiceNo] || [];
    const disburseIds = selected.map((item) => item.disburseId);
    const penaltyAmount = penaltyAmounts[invoiceNo] || 0;
    const additionalPayout = additionalPayoutAmounts[invoiceNo] || 0;

    setSaving(true);

    try {
      const token = Cookies.get("jwt_token");

      // TODO: dsaId-specific recovery-save endpoint pending — currently always posts to makeRecoveryInvoiceUrl
      const recoveryRes = await fetch(URLs.dmaSaveRecoveryUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          disburseIds,
          invoiceNo,
          penalityAmount: penaltyAmount,
          additionalPayout: additionalPayout,
        }),
      });

      if (!recoveryRes.ok) {
        const errorText = await recoveryRes.text();
        throw new Error(errorText || "Recovery failed");
      }

      const contentType = recoveryRes.headers.get("content-type");
      let recoveryResult;

      if (contentType && contentType.includes("application/json")) {
        recoveryResult = await recoveryRes.json();
        if (recoveryResult.success === false) {
          throw new Error(recoveryResult.message || "Recovery failed");
        }
      } else {
        recoveryResult = await recoveryRes.text();
      }
      await fetchInvoices();
      setSaveDialog({ open: false, invoiceNo: null });
      setExpandedRow(null);
      setError(null);
    } catch (err) {
      setError(err.message || "Operation failed");
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: COLORS.orange }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1 }, backgroundColor: "transparent" }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: COLORS.charcoal,
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 1,
          }}
        >
          <Receipt sx={{ color: COLORS.orange }} />
          Invoice Recovery Management
        </Typography>
        <Typography variant="body2" sx={{ color: COLORS.charcoal, fontWeight: 400 }}>
          Manage invoice recoveries by adding disbursements to invoices
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          border: `1px solid ${COLORS.charcoal}15`,
          overflow: "hidden",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        }}
      >
        <Table>
          <TableBody>
            <TableRow sx={{ backgroundColor: COLORS.charcoal, height: 60 }}>
              <TableCell sx={{ color: COLORS.white, fontWeight: 600, py: 2 }}>Date</TableCell>
              <TableCell sx={{ color: COLORS.white, fontWeight: 600, py: 2 }}>
                Invoice Number
              </TableCell>
              <TableCell sx={{ color: COLORS.white, fontWeight: 600, py: 2 }}>
                Financial Institution
              </TableCell>
              <TableCell align="right" sx={{ color: COLORS.white, fontWeight: 600, py: 2 }}>
                Amount
              </TableCell>
              <TableCell align="right" sx={{ color: COLORS.white, fontWeight: 600, py: 2 }}>
                TDS
              </TableCell>
              <TableCell align="right" sx={{ color: COLORS.white, fontWeight: 600, py: 2 }}>
                Net Amount
              </TableCell>
              <TableCell align="center" sx={{ color: COLORS.white, fontWeight: 600, py: 2 }}>
                Actions
              </TableCell>
            </TableRow>
            {invoices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((invoice) => {
              const isExpanded = expandedRow === invoice.invoiceNo;
              const summary = calculateSummary(
                invoice,
                selectedRecoveries[invoice.invoiceNo],
                penaltyAmounts[invoice.invoiceNo] || 0,
                additionalPayoutAmounts[invoice.invoiceNo] || 0
              );
              const selectedCount = selectedRecoveries[invoice.invoiceNo]?.length || 0;

              return (
                <React.Fragment key={invoice.invoiceNo}>
                  {/* Invoice Row */}
                  <TableRow
                    sx={{
                      backgroundColor: isExpanded ? `${COLORS.orange}08` : "inherit",
                      "&:hover": { backgroundColor: `${COLORS.orange}05` },
                      transition: "background-color 0.2s",
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {new Date(invoice.date).toLocaleDateString()}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {invoice.invoiceDisplayNumber}
                        </Typography>
                        <Typography variant="caption" sx={{ color: COLORS.darkGray }}>
                          {invoice.dsaName}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">{invoice.finainacialInstitutionName}</Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        ₹{invoice.amount.toLocaleString("en-IN")}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Box>
                        <Typography variant="body2">
                          ₹{invoice.tdsAmount.toLocaleString("en-IN")}
                        </Typography>
                        <Typography variant="caption" sx={{ color: COLORS.darkGray }}>
                          ({invoice.tdsPercent}%)
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 700, color: COLORS.primary }}>
                        ₹{invoice.netDsaAmount.toLocaleString("en-IN")}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={isExpanded ? <ExpandLessIcon /> : <AddIcon />}
                        onClick={() => handleExpandRow(invoice.invoiceNo)}
                        sx={{
                          backgroundColor: COLORS.orange,
                          color: COLORS.white,
                          borderRadius: 2,
                          px: 2,
                          py: 0.8,
                          fontWeight: 600,
                          textTransform: "none",
                          "&:hover": {
                            backgroundColor: COLORS.charcoal,
                            transform: "translateY(-1px)",
                            boxShadow: `0 4px 12px ${COLORS.orange}40`,
                          },
                          transition: "all 0.2s",
                        }}
                      >
                        {isExpanded ? "Hide" : "Recover"}
                        {selectedCount > 0 && (
                          <Badge badgeContent={selectedCount} color="primary" sx={{ ml: 1 }} />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>

                  {/* Expanded Recovery Section */}
                  <TableRow>
                    <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box
                          sx={{
                            p: { xs: 2, md: 3 },
                            backgroundColor: COLORS.lightGray,
                            borderTop: `2px solid ${COLORS.orange}`,
                          }}
                        >
                          <Grid container spacing={3}>
                            {/* Recovery Selection Card */}
                            <Grid item xs={12}>
                              <Card
                                sx={{
                                  borderRadius: 2,
                                  border: `1px solid ${COLORS.orange}20`,
                                  boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
                                }}
                              >
                                <CardContent>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      mb: 3,
                                      gap: 2,
                                    }}
                                  >
                                    <Avatar
                                      sx={{
                                        bgcolor: `${COLORS.orange}15`,
                                        color: COLORS.orange,
                                      }}
                                    >
                                      <TrendingDown />
                                    </Avatar>
                                    <Box>
                                      <Typography
                                        variant="h6"
                                        sx={{
                                          fontWeight: 600,
                                          color: COLORS.charcoal,
                                        }}
                                      >
                                        Add Recovery Disbursements
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: COLORS.darkGray }}>
                                        Select disbursements to recover from this invoice
                                      </Typography>
                                    </Box>
                                  </Box>

                                  {loadingRecoveries[invoice.invoiceNo] ? (
                                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                      <CircularProgress size={40} sx={{ color: COLORS.orange }} />
                                    </Box>
                                  ) : (
                                    <>
                                      <Autocomplete
                                        multiple
                                        options={getAvailableDisbursements(invoice.invoiceNo)}
                                        value={selectedRecoveries[invoice.invoiceNo] || []}
                                        loading={loadingRecoveries[invoice.invoiceNo]}
                                        isOptionEqualToValue={(option, value) =>
                                          option.disburseId === value.disburseId
                                        }
                                        getOptionLabel={(option) =>
                                          `${option.applicationNumber} - ${option.customerName}`
                                        }
                                        onChange={(event, newValue) =>
                                          handleRecoverySelect(invoice.invoiceNo, newValue)
                                        }
                                        filterOptions={(options, { inputValue }) =>
                                          options.filter(
                                            (option) =>
                                              option.applicationNumber
                                                ?.toLowerCase()
                                                .includes(inputValue.toLowerCase()) ||
                                              option.customerName
                                                ?.toLowerCase()
                                                .includes(inputValue.toLowerCase())
                                          )
                                        }
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            placeholder="Search by application number or customer name..."
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            InputProps={{
                                              ...params.InputProps,
                                              startAdornment: (
                                                <SearchIcon
                                                  sx={{ color: COLORS.darkGray, mr: 1 }}
                                                />
                                              ),
                                            }}
                                            sx={{
                                              "& .MuiOutlinedInput-root": {
                                                borderRadius: 2,
                                                backgroundColor: COLORS.white,
                                                "&:hover": {
                                                  borderColor: COLORS.orange,
                                                },
                                              },
                                            }}
                                          />
                                        )}
                                        renderTags={(value, getTagProps) => (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              flexWrap: "wrap",
                                              gap: 1,
                                              mt: 2,
                                            }}
                                          >
                                            {value.map((option, index) => (
                                              <Chip
                                                key={option.disburseId}
                                                label={
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: 0.5,
                                                    }}
                                                  >
                                                    <Typography
                                                      variant="caption"
                                                      sx={{ fontWeight: 500 }}
                                                    >
                                                      {option.applicationNumber}
                                                    </Typography>
                                                    <Typography
                                                      variant="caption"
                                                      sx={{ color: COLORS.orange, fontWeight: 600 }}
                                                    >
                                                      ₹
                                                      {option.grossPayoutAmount?.toLocaleString(
                                                        "en-IN"
                                                      )}
                                                    </Typography>
                                                  </Box>
                                                }
                                                onDelete={() =>
                                                  handleRemoveRecovery(
                                                    invoice.invoiceNo,
                                                    option.disburseId
                                                  )
                                                }
                                                sx={{
                                                  backgroundColor: `${COLORS.orange}10`,
                                                  color: COLORS.charcoal,
                                                  borderRadius: 2,
                                                  "& .MuiChip-deleteIcon": {
                                                    color: COLORS.darkGray,
                                                    "&:hover": {
                                                      color: COLORS.error,
                                                    },
                                                  },
                                                }}
                                              />
                                            ))}
                                          </Box>
                                        )}
                                        renderOption={(props, option) => (
                                          <li {...props}>
                                            <Box sx={{ width: "100%", py: 1 }}>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  justifyContent: "space-between",
                                                  alignItems: "center",
                                                }}
                                              >
                                                <Box>
                                                  <Typography
                                                    variant="body2"
                                                    sx={{ fontWeight: 600 }}
                                                  >
                                                    {option.applicationNumber}
                                                  </Typography>
                                                  <Typography
                                                    variant="caption"
                                                    sx={{ color: COLORS.darkGray }}
                                                  >
                                                    <AccountCircle sx={{ fontSize: 12, mr: 0.5 }} />
                                                    {option.customerName}
                                                  </Typography>
                                                </Box>
                                                <Typography
                                                  variant="body2"
                                                  sx={{
                                                    fontWeight: 700,
                                                    color: COLORS.orange,
                                                  }}
                                                >
                                                  ₹
                                                  {option.grossPayoutAmount?.toLocaleString(
                                                    "en-IN"
                                                  )}
                                                </Typography>
                                              </Box>
                                            </Box>
                                          </li>
                                        )}
                                      />
                                    </>
                                  )}
                                </CardContent>
                              </Card>
                            </Grid>

                            {/* Selected Recoveries */}
                            {selectedRecoveries[invoice.invoiceNo]?.length > 0 && (
                              <Grid item xs={12}>
                                <Card
                                  sx={{
                                    borderRadius: 2,
                                    border: `1px solid ${COLORS.success}20`,
                                    boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
                                  }}
                                >
                                  <CardContent>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        mb: 2,
                                      }}
                                    >
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <CheckCircleIcon sx={{ color: COLORS.success }} />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                          Selected Disbursements ({selectedCount})
                                        </Typography>
                                      </Box>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 700,
                                          color: COLORS.charcoal,
                                          backgroundColor: `${COLORS.success}10`,
                                          px: 2,
                                          py: 0.5,
                                          borderRadius: 1,
                                        }}
                                      >
                                        Total: ₹
                                        {selectedRecoveries[invoice.invoiceNo]
                                          .reduce(
                                            (sum, item) => sum + (item.grossPayoutAmount || 0),
                                            0
                                          )
                                          .toLocaleString("en-IN")}
                                      </Typography>
                                    </Box>

                                    <Grid container spacing={2}>
                                      {selectedRecoveries[invoice.invoiceNo].map((recovery) => (
                                        <Grid item xs={12} sm={6} md={4} key={recovery.disburseId}>
                                          <Card
                                            variant="outlined"
                                            sx={{
                                              borderRadius: 2,
                                              borderColor: `${COLORS.orange}30`,
                                              "&:hover": {
                                                borderColor: COLORS.orange,
                                                boxShadow: `0 2px 8px ${COLORS.orange}20`,
                                              },
                                            }}
                                          >
                                            <CardContent sx={{ p: 2 }}>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  justifyContent: "space-between",
                                                  alignItems: "flex-start",
                                                }}
                                              >
                                                <Box>
                                                  <Typography
                                                    variant="subtitle2"
                                                    sx={{ fontWeight: 700, color: COLORS.charcoal }}
                                                  >
                                                    {recovery.applicationNumber}
                                                  </Typography>
                                                  <Typography
                                                    variant="caption"
                                                    sx={{
                                                      color: COLORS.darkGray,
                                                      display: "block",
                                                    }}
                                                  >
                                                    {recovery.customerName}
                                                  </Typography>
                                                  <Typography
                                                    variant="caption"
                                                    sx={{
                                                      color: COLORS.darkGray,
                                                      display: "block",
                                                    }}
                                                  >
                                                    Provider: {recovery.providerName}
                                                  </Typography>
                                                </Box>
                                              </Box>
                                              <Divider sx={{ my: 1.5 }} />
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  justifyContent: "space-between",
                                                  alignItems: "center",
                                                }}
                                              >
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                  }}
                                                >
                                                  <Typography
                                                    component="span"
                                                    sx={{
                                                      fontSize: 16,
                                                      fontWeight: 600,
                                                      color: COLORS.orange,
                                                    }}
                                                  >
                                                    ₹
                                                  </Typography>

                                                  <Typography
                                                    variant="body2"
                                                    sx={{ color: COLORS.darkGray }}
                                                  >
                                                    Amount
                                                  </Typography>
                                                </Box>
                                                <Typography
                                                  variant="h6"
                                                  sx={{
                                                    fontWeight: 700,
                                                    color: COLORS.orange,
                                                  }}
                                                >
                                                  ₹
                                                  {recovery.grossPayoutAmount?.toLocaleString(
                                                    "en-IN"
                                                  )}
                                                </Typography>
                                              </Box>
                                            </CardContent>
                                          </Card>
                                        </Grid>
                                      ))}
                                    </Grid>
                                  </CardContent>
                                </Card>
                              </Grid>
                            )}

                            {/* Summary Section - Always Visible */}
                            <Grid item xs={12}>
                              <Card
                                sx={{
                                  borderRadius: 2,
                                  border: `2px solid ${COLORS.orange}20`,
                                  backgroundColor: `${COLORS.charcoal}03`,
                                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                                }}
                              >
                                <CardContent>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      mb: 3,
                                      gap: 2,
                                    }}
                                  >
                                    <Avatar
                                      sx={{
                                        bgcolor: `${COLORS.orange}15`,
                                        color: COLORS.orange,
                                      }}
                                    >
                                      <CalculateIcon />
                                    </Avatar>
                                    <Typography
                                      variant="h6"
                                      sx={{ fontWeight: 700, color: COLORS.charcoal }}
                                    >
                                      Recovery Summary
                                    </Typography>
                                  </Box>

                                  <Grid container spacing={2} sx={{ mb: 3 }}>
                                    {[
                                      {
                                        label: "Original Amount",
                                        value: `₹${invoice.amount.toLocaleString("en-IN")}`,
                                        color: COLORS.charcoal,
                                        icon: <Receipt />,
                                      },
                                      {
                                        label: "Additional Payout",
                                        value: additionalPayoutAmounts[invoice.invoiceNo] || 0,
                                        color: COLORS.success,
                                        icon: <AddCircleOutline />,
                                        isInput: true,
                                      },
                                      {
                                        label: "Total Recovery",
                                        value: `- ₹${summary.totalGrossPayout.toLocaleString(
                                          "en-IN"
                                        )}`,
                                        color: COLORS.error,
                                        icon: <TrendingDown />,
                                      },
                                      {
                                        label: "Penalty Amount",
                                        value: penaltyAmounts[invoice.invoiceNo] || 0,
                                        color: COLORS.warning,
                                        icon: <GavelIcon />,
                                        isInput: true,
                                      },
                                      {
                                        label: "Remarks",
                                        value: remarksData[invoice.invoiceNo] || "",
                                        color: COLORS.info,
                                        icon: <CommentIcon />,
                                        isInput: true,
                                      },

                                      {
                                        label: "Amount After Deductions",
                                        value: `₹${summary.amountAfterDeductions.toLocaleString(
                                          "en-IN"
                                        )}`,
                                        color: COLORS.primary,
                                        icon: (
                                          <CurrencyRupeeIcon
                                            sx={{ fontSize: 18, color: COLORS.primary }}
                                          />
                                        ),
                                      },
                                      {
                                        label: "GST @18%",
                                        value: `+ ₹${summary.gst.toLocaleString("en-IN")}`,
                                        color: COLORS.darkGray,
                                        icon: <CalculateIcon />,
                                      },
                                    ].map((item, index) => (
                                      <Grid item xs={12} sm={6} md={2} key={index}>
                                        <Card
                                          sx={{
                                            borderRadius: 2,
                                            border: `1px solid ${item.color}20`,
                                            backgroundColor: COLORS.white,
                                            height: "100%",
                                          }}
                                        >
                                          <CardContent sx={{ p: 2, textAlign: "center" }}>
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                mb: 1,
                                                color: item.color,
                                              }}
                                            >
                                              {item.icon}
                                            </Box>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: COLORS.darkGray,
                                                fontWeight: 500,
                                                display: "block",
                                                mb: item.isInput ? 1 : 0.5,
                                              }}
                                            >
                                              {item.label}
                                            </Typography>

                                            {item.isInput ? (
                                              // Input field
                                              <TextField
                                                type={item.label === "Remarks" ? "text" : "number"}
                                                size="small"
                                                value={item.value}
                                                onChange={(e) => {
                                                  if (item.label === "Penalty Amount") {
                                                    handlePenaltyChange(
                                                      invoice.invoiceNo,
                                                      e.target.value
                                                    );
                                                  } else if (item.label === "Additional Payout") {
                                                    handleAdditionalPayoutChange(
                                                      invoice.invoiceNo,
                                                      e.target.value
                                                    );
                                                  } else if (item.label === "Remarks") {
                                                    handleRemarksChange(
                                                      invoice.invoiceNo,
                                                      e.target.value
                                                    );
                                                  }
                                                }}
                                                InputProps={
                                                  item.label !== "Remarks"
                                                    ? {
                                                        startAdornment: (
                                                          <CurrencyRupeeIcon
                                                            sx={{
                                                              fontSize: 16,
                                                              color: item.color,
                                                              mr: 0.5,
                                                            }}
                                                          />
                                                        ),
                                                      }
                                                    : {}
                                                }
                                                sx={{
                                                  "& .MuiOutlinedInput-root": {
                                                    "& input": {
                                                      textAlign:
                                                        item.label === "Remarks"
                                                          ? "left"
                                                          : "center",
                                                      fontWeight: 700,
                                                      color: item.color,
                                                    },
                                                    "&:hover": {
                                                      borderColor: item.color,
                                                    },
                                                  },
                                                }}
                                              />
                                            ) : (
                                              // Regular text display
                                              <Typography
                                                variant="h6"
                                                sx={{
                                                  color: item.color,
                                                  fontWeight: 700,
                                                  lineHeight: 1.2,
                                                }}
                                              >
                                                {item.value}
                                              </Typography>
                                            )}

                                            {item.isInput && (
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: COLORS.darkGray,
                                                  display: "block",
                                                  mt: 0.5,
                                                  fontStyle: "italic",
                                                }}
                                              >
                                                {item.label === "Penalty Amount"
                                                  ? "Enter penalty amount"
                                                  : item.label === "Additional Payout"
                                                  ? "Enter additional payout"
                                                  : "Enter remarks"}
                                              </Typography>
                                            )}
                                          </CardContent>
                                        </Card>
                                      </Grid>
                                    ))}
                                  </Grid>

                                  {/* Final Amount */}
                                  <Box
                                    sx={{
                                      textAlign: "center",
                                      p: 3,
                                      borderRadius: 2,
                                      backgroundColor: COLORS.white,
                                      border: `2px solid ${COLORS.orange}30`,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: COLORS.darkGray,
                                        textTransform: "uppercase",
                                        letterSpacing: 1,
                                        mb: 1,
                                      }}
                                    >
                                      Final Payable Amount
                                    </Typography>
                                    <Typography
                                      variant="h5"
                                      sx={{
                                        color: COLORS.orange,
                                        fontWeight: 800,
                                        letterSpacing: "-0.5px",
                                        mb: 0.5,
                                      }}
                                    >
                                      ₹{summary.totalAmount.toLocaleString("en-IN")}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: COLORS.darkGray,
                                        fontStyle: "italic",
                                      }}
                                    >
                                      {summary.totalDeductions > 0 ||
                                      summary.additionalPayoutAmount > 0
                                        ? `Adjustments: Recovery -₹${summary.totalGrossPayout.toLocaleString(
                                            "en-IN"
                                          )}, Penalty -₹${summary.penaltyAmount.toLocaleString(
                                            "en-IN"
                                          )}, Additional Payout +₹${summary.additionalPayoutAmount.toLocaleString(
                                            "en-IN"
                                          )}`
                                        : "No adjustments applied to this invoice"}
                                    </Typography>
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>

                            {/* Action Buttons */}
                            <Grid item xs={12}>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: { xs: "column", sm: "row" },
                                  gap: 2,
                                  justifyContent: "center",
                                  alignItems: "center",
                                  pt: 3,
                                  borderTop: `1px solid ${COLORS.charcoal}10`,
                                }}
                              >
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<RemoveRedEyeIcon />}
                                  onClick={() => handleViewInvoicePdf(invoice.invoiceNo)}
                                  sx={{
                                    color: COLORS.primary,
                                    borderColor: COLORS.primary,
                                    borderRadius: 2,
                                    px: 3,
                                    py: 1.5,
                                    fontWeight: 500,
                                    minWidth: { xs: "100%", sm: 160 },
                                    "&:hover": {
                                      borderColor: COLORS.primary,
                                      backgroundColor: `${COLORS.primary}05`,
                                    },
                                  }}
                                >
                                  View Invoice
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<RemoveRedEyeIcon />}
                                  onClick={() => handleViewCreditNoteInvoicePdf(invoice.invoiceNo)}
                                  sx={{
                                    color: COLORS.primary,
                                    borderColor: COLORS.primary,
                                    borderRadius: 2,
                                    px: 3,
                                    py: 1.5,
                                    fontWeight: 500,
                                    minWidth: { xs: "100%", sm: 160 },
                                    "&:hover": {
                                      borderColor: COLORS.primary,
                                      backgroundColor: `${COLORS.primary}05`,
                                    },
                                  }}
                                >
                                  View Credit Note Invoice
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<CancelIcon />}
                                  onClick={() => setExpandedRow(null)}
                                  sx={{
                                    color: COLORS.charcoal,
                                    borderColor: COLORS.charcoal,
                                    borderRadius: 2,
                                    px: 4,
                                    py: 1.5,
                                    fontWeight: 600,
                                    minWidth: { xs: "100%", sm: 160 },
                                    "&:hover": {
                                      borderColor: COLORS.orange,
                                      color: COLORS.orange,
                                      backgroundColor: `${COLORS.orange}05`,
                                    },
                                  }}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={
                                    saving ? (
                                      <CircularProgress size={20} color="inherit" />
                                    ) : (
                                      <SaveIcon />
                                    )
                                  }
                                  disabled={saving}
                                  onClick={() =>
                                    setSaveDialog({ open: true, invoiceNo: invoice.invoiceNo })
                                  }
                                  sx={{
                                    backgroundColor: COLORS.orange,
                                    color: COLORS.white,
                                    borderRadius: 2,
                                    px: 4,
                                    py: 1.5,
                                    fontWeight: 600,
                                    minWidth: { xs: "100%", sm: 200 },
                                    "&:hover": {
                                      backgroundColor: COLORS.charcoal,
                                      transform: "translateY(-2px)",
                                      boxShadow: `0 6px 20px ${COLORS.orange}40`,
                                    },
                                    "&:disabled": {
                                      backgroundColor: `${COLORS.orange}50`,
                                    },
                                  }}
                                >
                                  {saving ? "Saving Changes..." : "Save Recovery"}
                                </Button>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={invoices.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: `1px solid ${COLORS.charcoal}15`,
          }}
        />
      </TableContainer>

      <Dialog
        open={saveDialog.open}
        onClose={() => setSaveDialog({ open: false, invoiceNo: null })}
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxWidth: 500,
            width: "100%",
          },
        }}
      >
        <DialogTitle
          sx={{
            color: COLORS.charcoal,
            fontWeight: 600,
            fontSize: "1.125rem",
            pb: 2.5,
            pt: 3,
            px: 3,
          }}
        >
          Save Recoveries
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 2.5 }}>
          <Alert
            severity="info"
            sx={{
              mb: 2.5,
              borderRadius: 1.5,
              fontSize: "0.8125rem",
              py: 0.75,
              "& .MuiAlert-message": { py: 0.5 },
            }}
            icon={<InfoIcon fontSize="small" />}
          >
            Selected disbursements will be saved as recoveries for this invoice.
          </Alert>

          <Typography
            sx={{
              fontSize: "0.875rem",
              color: COLORS.charcoal,
              lineHeight: 1.5,
            }}
          >
            Save {selectedRecoveries[saveDialog.invoiceNo]?.length || 0} recovery disbursement
            {selectedRecoveries[saveDialog.invoiceNo]?.length !== 1 ? "s" : ""}?
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2.5,
            pt: 2,
            px: 3,
            gap: 1.5,
          }}
        >
          <Button
            size="small"
            variant="outline"
            onClick={() => setSaveDialog({ open: false, invoiceNo: null })}
            sx={{
              color: COLORS.darkGray,
              fontWeight: 500,
              fontSize: "0.8125rem",
              px: 2.5,
              py: 0.75,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleSave(saveDialog.invoiceNo)}
            variant="contained"
            disabled={saving}
            size="small"
            sx={{
              backgroundColor: COLORS.orange,
              color: COLORS.white,
              fontWeight: 600,
              fontSize: "0.8125rem",
              px: 3,
              py: 0.75,
              borderRadius: 1.5,
              minWidth: 120,
              "&:hover": {
                backgroundColor: COLORS.charcoal,
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              },
              "&:active": { transform: "translateY(0)" },
              transition: "all 0.2s ease",
            }}
          >
            {saving ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Saving...
              </Box>
            ) : (
              "Confirm"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

InvoiceEditor.propTypes = {
  dsaId: PropTypes.number,
};

InvoiceEditor.defaultProps = {
  dsaId: 0,
};
export default InvoiceEditor;
